import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchService } from 'app/services/search.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AbstractPickedFile, TemplatePickedFile, CloudPickedFile } from 'app/models/picked-file';
import { FormTemplate } from 'app/models/template';
import { Storage } from 'app/models/storage';
import { FieldsPickerComponent } from 'app/views/fields-picker/fields-picker.component';
import { FormTemplateService } from 'app/services/form-template.service';
import { finalize, map } from 'rxjs/operators';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { DeletedFilesSearchEnum, SearchResponse, SearchResult } from 'app/models/search-results';
import { ToastService } from 'ng-uikit-pro-standard';
import { FileExplorerService } from 'app/services/file-explorer.service';
import { FormFillerComponent } from 'app/views/form-filler/form-filler.component';
import { EcoDocsTitleService } from 'app/services/ecodocs-title.service';
import { FileClass } from 'app/models/file-class';
import { CentralMetadataService } from 'app/services/central-metadata.service';
import { FileClassService } from 'app/services/file-class.service';
import * as _ from "lodash";
import { CentralMetadata } from 'app/models/central-metadata';
import { DateRangeService } from 'app/services/date-range.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {

  folders: any[];
  collapsed: boolean = true;
  
  isLoadingResults: boolean = false;
  isFormLoading: boolean = false;
  cachedForm: FormTemplate = null;

  searchRequestFormGroup: FormGroup = null;
  searchResponse: SearchResponse = null;
  
  @ViewChild('fieldsPicker') fieldsPicker: FieldsPickerComponent;
  @ViewChild('resultsContainer', {static: false}) resultsTableElementRef: ElementRef;
  @ViewChild('formFiller') formFiller: FormFillerComponent;

  fileClasses: FileClass[] = [];

  builtInMetadata: CentralMetadata[] =[
    { key: 'modified_by', name: 'Modified By', type: 'textfield'} as CentralMetadata,
    { key: 'workflow', name: 'Workflow', type: 'textfield'} as CentralMetadata,
    { key: 'modified', name: 'Last Modified', type: 'datetime'} as CentralMetadata,
    { key: 'file_class', name: 'File Class', type: 'textfield'} as CentralMetadata,
  ];

  uniqueMergedMetadata: CentralMetadata[] = [];

  constructor(private validationHandler: ValidationHandler, private activatedRoute: ActivatedRoute, 
      private searchService: SearchService, private formTemplateService: FormTemplateService,
      private toastService: ToastService, private fileExplorerService: FileExplorerService,
      private router: Router, private ecoDocsTitleService: EcoDocsTitleService,
      private centralMetadataService: CentralMetadataService, private fileClassService: FileClassService,
      private formBuilder: FormBuilder, public dateRangeService: DateRangeService) {

        this.uniqueMergedMetadata = [...this.builtInMetadata];

        this.uniqueMergedMetadata.forEach((m) => {
          m.isLoadingValues = true;
          this.centralMetadataService.fieldValues(m.key).pipe(finalize(() => {
            m.isLoadingValues = false;
          })).subscribe((s) => { this.uniqueMergedMetadata.find((v) => v.key == m.key).values = s; });
        });

        this.fileClassService.list().subscribe((f) => this.fileClasses = f);

        this.activatedRoute.queryParams.subscribe((queryParams) => {
          this.searchRequestFormGroup = this.searchService.searchRequestFormGroup(queryParams['options']?JSON.parse(queryParams['options']): {
            is_files: true,
            is_user_storage: true,
            is_global_storage: true,
            in_file_contents: true,
            in_all_file_versions: false,
            deleted_search_type: DeletedFilesSearchEnum.EXCLUDE_DELETED,
            is_forms: true,
            in_form_contents: true,
            is_data: true
          });
          
          this.searchRequestFormGroup.get('metadata.file_class').valueChanges.subscribe((newValue: string[]) => {
            console.log('File Class Changed');
            newValue = newValue || [];
            
            let newMergedMetadata: CentralMetadata[] = [];
            
            this.fileClasses.filter((f) => newValue.includes(f.name)).forEach((f) => {
              newMergedMetadata = _.unionBy(newMergedMetadata, f.required_metadata, f.optional_metadata, 'key');
            });
          
            let metadataFormGroup: FormGroup = this.searchRequestFormGroup.get('metadata') as FormGroup;
          
            // handle builtInMetadata
            this.builtInMetadata.forEach((b) => {
              if (!metadataFormGroup.contains(b.key)) {
                metadataFormGroup.addControl(b.key, new FormControl());
              }
            });
          
            // copy ranges from same metadata that was previously selected
            newMergedMetadata.forEach((m) => {
              let previousMetadata = this.uniqueMergedMetadata.find((s) => s.key == m.key);
              if (previousMetadata) {
                m.values = previousMetadata.values;
              } else {
                m.isLoadingValues = true;
                this.centralMetadataService.fieldValues(m.key).pipe(finalize(() => {
                  m.isLoadingValues = false;
                })).subscribe((s) => { m.values = s; });
              }
          
              let controlValue = this.searchRequestFormGroup.get('metadata.' + m.key)?.value || ((m.type == 'number' || m.type == 'currency') ? { first: null, second: null } : null);
              
              if (metadataFormGroup.contains(m.key)) {
                metadataFormGroup.get(m.key).setValue(controlValue);
              } else {
                metadataFormGroup.addControl(m.key, new FormControl(controlValue));
              }
            });
          
            newMergedMetadata = [...this.builtInMetadata, ...newMergedMetadata];
          
            metadataFormGroup.updateValueAndValidity();
          
            this.uniqueMergedMetadata = newMergedMetadata;
          });

          this.collapsed = this.searchRequestFormGroup.value.query;
      
          this.searchRequestFormGroup.get('data_forms').valueChanges.subscribe((v) => {
            if (v?.length != 1) {
              this.searchRequestFormGroup.get('data_fields').reset();
            }
          });
      
          if (this.searchRequestFormGroup.value.query) {
            this.onSearchSubmit();
          }
        });
    }

  ngOnInit(): void {
    this.ecoDocsTitleService.setTitle('Search', false);
  }
  
  onErrLoadIcon(e: Event) {
    e.srcElement["src"] = "/assets/img/file-icons/unknown.png";
  }

  toggleCard(){
    this.collapsed = !this.collapsed;
  }

  onPickedTemplates(e: AbstractPickedFile[]) {
    let pickedTemplates = e as TemplatePickedFile[];

    let dataForms: TemplatePickedFile[] = this.searchRequestFormGroup.get('data_forms').value || [];
    dataForms.push(...pickedTemplates);
    dataForms = dataForms.filter((v, index, self) => {
      return self.findIndex((value) => {
        return value.id == v.id;
      }) === index;
    });
    this.searchRequestFormGroup.get('data_forms').setValue(dataForms);
  }

  onPickedFormFolders(e: AbstractPickedFile[]) {
    let pickedFormFolders = e as TemplatePickedFile[];
    
    let formFolders: TemplatePickedFile[] = this.searchRequestFormGroup.get('forms_folders').value || [];
    formFolders.push(...pickedFormFolders);
    formFolders = formFolders.filter((v, index, self) => {
      return self.findIndex((value) => {
        return value.path == v.path;
      }) === index;
    });
    this.searchRequestFormGroup.get('forms_folders').setValue(formFolders);
  }

  onPickedDataFolders(e: AbstractPickedFile[]) {
    let pickedFormFolders = e as TemplatePickedFile[];

    let dataFolders: TemplatePickedFile[] = this.searchRequestFormGroup.get('data_folders').value || [];
    dataFolders.push(...pickedFormFolders);
    dataFolders = dataFolders.filter((v, index, self) => {
      return self.findIndex((value) => {
        return value.path == v.path;
      }) === index;
    });
    this.searchRequestFormGroup.get('data_folders').setValue(dataFolders);
  }

  onPickedFolders(e: AbstractPickedFile[], controlName: string) {
    let pickedUserFolders = e as CloudPickedFile[];

    let userFolders: CloudPickedFile[] = this.searchRequestFormGroup.get(controlName).value || [];
    userFolders.push(...pickedUserFolders);
    userFolders = userFolders.filter((v, index, self) => {
      return self.findIndex((value) => {
        return value.path == v.path;
      }) === index;
    });
    this.searchRequestFormGroup.get(controlName).setValue(userFolders);
  }


  onPickedFields(fields: string[]) {
    this.searchRequestFormGroup.get('data_fields').setValue(fields);
  }

  onPickFields() {
    if (this.cachedForm?.id == this.searchRequestFormGroup.value.data_forms[0].id) {
      this.fieldsPicker.showPicker(this.cachedForm.schema, this.searchRequestFormGroup.value.data_fields);
    } else {
      this.isFormLoading = true;
      this.formTemplateService.getById(this.searchRequestFormGroup.value.data_forms[0].id).pipe(finalize(() => {
        this.isFormLoading = false;
      })).subscribe((f) => {
        this.cachedForm = f;
        this.fieldsPicker.showPicker(this.cachedForm.schema, this.searchRequestFormGroup.value.data_fields);
      });
    }
  }

  globalStorageFilter(storages: Storage[]) {
    return storages.filter(s => s.display_name == 'Global');
  }

  userStorageFilter(storages: Storage[]) {
    return storages.filter(s => s.display_name == 'Private');
  }

  onSearchSubmit() {
    this.validationHandler.onSubmitCheck(this.searchRequestFormGroup);
    if (!this.searchRequestFormGroup.valid) {
      window.scroll(0,0);
      return;
    }

    this.searchRequestFormGroup.get('from').setValue(0);
    this.isFormLoading = true;
    this.searchService.search(this.searchRequestFormGroup.value).pipe(finalize(() => {
      this.isFormLoading = false;
    })).subscribe((s) => {
      this.searchResponse = s;
      // give angular time to detect changes and render the datatable before scrolling to it
      setTimeout(() => {
        //window.scrollTo({top: this.resultsTableElementRef.nativeElement.scrollHeight, behavior: "smooth"});
      }, 100);
    }, () => {
      this.toastService.error('Error getting results!');
    });
  }

  setPage(pageInfo) {
    this.searchRequestFormGroup.get('from').setValue(pageInfo.offset * 10);
    this.isLoadingResults = true;
    this.searchService.search(this.searchRequestFormGroup.value).pipe(finalize(() => {
      this.isLoadingResults = false;
    })).subscribe((s) => {
      this.searchResponse = s;
    }, () => {
      this.toastService.error('Error getting results!');
    });
  }

  downloadOrViewSearchResult(searchResult: SearchResult) {
    if (searchResult.type == 'file') {
      if (this.isFolder(searchResult)) {
        this.fileExplorerService.downloadFolder(searchResult.file_path, searchResult.storage.id);
      } else {
        this.fileExplorerService.downloadFile(searchResult.file_path, searchResult.storage.id);
      }
    } else if (searchResult.type == 'form') {
      this.isFormLoading = true;
      this.formTemplateService.getById(searchResult.id).pipe(finalize(() => {
        this.isFormLoading = false;
      })).subscribe((f) => {
        this.formFiller.show(f, null, null, [], true, false, true);
      });
    } else if (searchResult.type == 'data') {
      this.isFormLoading = true;
      this.formTemplateService.getSubmissionById(searchResult.id).pipe(finalize(() => {
        this.isFormLoading = false;
      })).subscribe((f) => {
        this.formFiller.show(f.template, f, null, null, false, true, true);
      });
    }
  }

  openContainingFolder(searchResult: SearchResult) {
    if (searchResult.type == 'file') {
      window.open(this.router.createUrlTree(['file-explorer', searchResult.storage.id, searchResult.file_path.substr(0, searchResult.file_path.lastIndexOf('/'))], {}).toString());
    } else if (searchResult.type == 'form') {
      window.open(this.router.createUrlTree(['forms-explorer', searchResult.file_path.substr(0, searchResult.file_path.lastIndexOf('/'))], {}).toString());
    } else if (searchResult.type == 'data') {
      window.open(this.router.createUrlTree(['form-data', searchResult.template_id], {}).toString());
    }
  }

  isFolder(searchResult: SearchResult) : boolean {
    return searchResult.file_path.substr(searchResult.file_path.length - 1) == '/';
  }

  isLoadingAnyMetadata() : boolean {
    let result: boolean = false;
    this.uniqueMergedMetadata.forEach((m) => {
      result = result || m.isLoadingValues;
    });
    return result;
  }
}
